import React, { useState } from 'react';
import { errorMsg } from 'src/components/utilities/utils';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
type MemberSummary = {
    firstName?: string;
    lastName?: string;
    email?: string;
    profileURL?: string;
};

type Props = {
    member: MemberSummary;
    memberToken: string;
    onClose: () => void;
};

const ImpersonateLinkModal: React.FC<Props> = ({ member, onClose }) => {
    const [linkCopied, setLinkCopied] = useState(false);
    const {
        memberToken,
    } = useSelector((state: RootState) => ({
        memberToken: state.users.memberToken,
    }));

    const handleCopyLink = async () => {
        if (!memberToken) return;
        try {
            await navigator.clipboard.writeText(memberToken);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            errorMsg('Unable to copy link');
        }
    };

    const handleOpenInNewTab = () => {
        if (memberToken) {
            window.open(memberToken, '_blank', 'noopener,noreferrer');
        }
    };


    return (
        <div
            className={`modal fade fixed top-0 left-0 hidden w-full h-full outline-none overflow-x-hidden overflow-y-auto modal-open`}
            id="ImpersonateLinkModal"
            aria-labelledby="ImpersonateLinkModalLabel"
            aria-hidden="true">
            <div className="modal-dialog relative w-auto pointer-events-none max-w-xl">
                <div className="modal-content border-none shadow-lg relative flex flex-col w-full pointer-events-auto bg-white bg-clip-padding rounded-md outline-none text-current">
                    <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 pb-2 rounded-t-md">
                        <button
                            onClick={onClose}
                            type="button"
                            className="dismiss-btn"
                            data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>

                    <div className="modal-body px-4 pb-4">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="theme-light-clr site-clr h-9 w-9 flex items-center justify-center rounded-full">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                </svg>
                            </span>
                            <div>
                                <h3 className="font-semibold text-lg">Login Link Generated</h3>
                                <p className="text-sm text-gray-500">
                                    Use the link below to access Webcastcloud as this member.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 bg-gray-100 rounded-md p-3 mb-4">
                            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                                <img
                                    className="w-full h-full object-cover"
                                    src={member.profileURL || '../../assets/img/user-png.png'}
                                />
                            </div>
                            <div>
                                <p className="font-semibold text-sm">
                                    {member.firstName} {member.lastName}
                                </p>
                                <p className="text-xs text-gray-500">{member.email}</p>
                            </div>
                        </div>

                        <label className="text-sm font-semibold block mb-1">Impersonation Login Link</label>
                        <div className="flex items-center gap-2 border rounded-md px-3 py-2 mb-4">
                            <input
                                type="text"
                                readOnly
                                value={memberToken || ''}
                                className="flex-1 text-sm outline-none bg-transparent truncate"
                            />
                            <button type="button" onClick={handleCopyLink} className="text-gray-500 hover:text-gray-700">
                                <i className="fas fa-copy"></i>
                            </button>
                        </div>

                        <div className="flex gap-3 mb-4">
                            <button type="button" onClick={handleCopyLink} className="flex-1 border rounded-md py-2 text-sm font-semibold">
                                {linkCopied ? 'Copied!' : 'Copy Link'}
                            </button>
                            <button type="button" onClick={handleOpenInNewTab} className="flex-1 btn-primary py-2 text-sm">
                                Open in New Tab
                            </button>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3 text-xs text-blue-800">
                            <i className="fas fa-info-circle mt-0.5  mr-1"></i>
                            This link is valid for 50 minutes and can only be used once.
                        </div>

                        <div className="border border-amber-200 rounded-md p-3 mb-4 bg-yellow-50">
                            <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                                <i className="fas fa-exclamation-triangle text-yellow-500"></i> Important
                            </p>
                            <ul className="text-xs text-amber-800 space-y-1 list-disc pl-4">
                                <li>The member may be able to see that their account has been accessed.</li>
                                <li>{`Activity performed while logged in may be recorded against the member's account.`}</li>
                                <li>{`This will not change the member's Member Type, Member Lists, subscriptions, role or account configuration.`}</li>
                            </ul>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="button"
                                data-bs-dismiss="modal"
                                onClick={onClose}
                                className="px-4 py-2 rounded-md border font-semibold text-sm">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImpersonateLinkModal;